// src/pages/ForgotPasswordPage.tsx
import { useState } from 'react';
import { Link } from 'react-router-dom';
import apiClient from '../api/client';

export default function ForgotPasswordPage() {
    const [identifier, setIdentifier] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        setLoading(true);

        try {
            const response = await apiClient.post('/auth/forgot-password', { identifier });
            setSuccess(response.data.message);
            setIdentifier('');
        } catch (err: any) {
            if (err.response?.status === 429 && err.response?.data?.rateLimited) {
                const errorMsg = err.response.data.error || 'Por favor, aguarde antes de solicitar um novo e-mail.';
                const spamMsg = err.response.data.message || 'Verifique sua caixa de entrada e também a pasta de spam/lixo eletrônico.';
                setError(`⏰ ${errorMsg}\n\n📧 ${spamMsg}`);
            } else {
                setError(err.response?.data?.error || 'Erro ao solicitar recuperação de senha.');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-brand-blue-light via-background to-white flex items-center justify-center p-4">
            <div className="w-full max-w-6xl grid lg:grid-cols-2 gap-8 items-center">
                {/* Lado esquerdo - Ilustração/Informação */}
                <div className="hidden lg:flex flex-col justify-center space-y-6 px-8">
                    <div className="space-y-4">
                        <h2 className="text-4xl font-bold text-text-primary leading-tight">
                            Recupere o acesso à sua <span className="text-brand-blue">conta</span>
                        </h2>
                        <p className="text-lg text-text-secondary leading-relaxed">
                            Não se preocupe! Enviaremos um link de recuperação para o email cadastrado na sua conta.
                        </p>
                    </div>

                    {/* Cards informativos */}
                    <div className="space-y-4 pt-8">
                        <div className="flex items-start gap-4 bg-white/60 backdrop-blur-sm p-4 rounded-xl shadow-subtle">
                            <div className="flex-shrink-0 w-12 h-12 bg-brand-blue/10 rounded-lg flex items-center justify-center">
                                <svg className="w-6 h-6 text-brand-blue" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                </svg>
                            </div>
                            <div>
                                <h3 className="font-semibold text-text-primary mb-1">Rápido e Seguro</h3>
                                <p className="text-sm text-text-secondary">Processo simples em apenas alguns passos</p>
                            </div>
                        </div>

                        <div className="flex items-start gap-4 bg-white/60 backdrop-blur-sm p-4 rounded-xl shadow-subtle">
                            <div className="flex-shrink-0 w-12 h-12 bg-brand-blue/10 rounded-lg flex items-center justify-center">
                                <svg className="w-6 h-6 text-brand-blue" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                </svg>
                            </div>
                            <div>
                                <h3 className="font-semibold text-text-primary mb-1">Link por E-mail</h3>
                                <p className="text-sm text-text-secondary">Receba o link de recuperação no seu email</p>
                            </div>
                        </div>

                        <div className="flex items-start gap-4 bg-white/60 backdrop-blur-sm p-4 rounded-xl shadow-subtle">
                            <div className="flex-shrink-0 w-12 h-12 bg-brand-blue/10 rounded-lg flex items-center justify-center">
                                <svg className="w-6 h-6 text-brand-blue" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                            <div>
                                <h3 className="font-semibold text-text-primary mb-1">Válido por 1 Hora</h3>
                                <p className="text-sm text-text-secondary">Link expira em 60 minutos por segurança</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Lado direito - Formulário */}
                <div className="w-full">
                    <div className="bg-white p-8 md:p-10 rounded-2xl shadow-subtle-hover">
                        <div className="mb-8">
                            <h1 className="text-3xl font-bold text-text-primary mb-2">Esqueci minha senha</h1>
                            <p className="text-text-secondary">Digite seu email ou nome de usuário</p>
                        </div>

                        {error && (
                            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
                                <svg className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                </svg>
                                <div className="flex-1">
                                    {error.split('\n\n').map((line, index) => (
                                        <p key={index} className={`text-sm text-red-800 ${index > 0 ? 'mt-2 font-medium' : ''}`}>
                                            {line}
                                        </p>
                                    ))}
                                </div>
                            </div>
                        )}

                        {success && (
                            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl flex items-start gap-3">
                                <svg className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                                <p className="text-sm text-green-800 flex-1">{success}</p>
                            </div>
                        )}

                        <form onSubmit={handleSubmit} className="space-y-5">
                            <div>
                                <label htmlFor="identifier" className="block text-sm font-semibold text-text-primary mb-2">
                                    Email ou Nome de Usuário
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                        <svg className="h-5 w-5 text-text-secondary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                        </svg>
                                    </div>
                                    <input
                                        type="text"
                                        id="identifier"
                                        value={identifier}
                                        onChange={e => setIdentifier(e.target.value)}
                                        required
                                        className="w-full pl-12 pr-4 py-3 border border-border-color rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-blue focus:border-transparent transition-all"
                                        placeholder="seu@email.com ou seu_usuario"
                                        disabled={loading || !!success}
                                    />
                                </div>
                            </div>

                            <button
                                type="submit"
                                disabled={loading || !!success}
                                className="w-full bg-brand-blue text-white py-3.5 rounded-xl font-semibold hover:bg-blue-700 transition-all disabled:bg-gray-400 disabled:cursor-not-allowed shadow-sm hover:shadow-md transform hover:-translate-y-0.5 active:translate-y-0"
                            >
                                {loading ? (
                                    <span className="flex items-center justify-center gap-2">
                                        <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                        </svg>
                                        Enviando...
                                    </span>
                                ) : 'Enviar Link de Recuperação'}
                            </button>
                        </form>

                        <div className="mt-8 pt-6 border-t border-border-color space-y-3">
                            <p className="text-center text-sm text-text-secondary">
                                Lembrou sua senha?{' '}
                                <Link to="/login" className="font-semibold text-brand-blue hover:text-blue-700 hover:underline transition-colors">
                                    Fazer login
                                </Link>
                            </p>
                            <p className="text-center text-sm text-text-secondary">
                                Não tem uma conta?{' '}
                                <Link to="/register" className="font-semibold text-brand-blue hover:text-blue-700 hover:underline transition-colors">
                                    Criar conta
                                </Link>
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

